/* eslint-disable func-names, prefer-rest-params, space-before-function-paren */
import { registerClass } from '../SatCoreRegistry';

import googleAnalyticsManager from '../managers/GoogleAnalyticsManager';

/**
 * Service for initializing and managing Google Analytics tracking.
 *
 * This service handles the initialization of Google Analytics (gtag.js) and provides
 * helper methods for tracking educational platform events.
 *
 * **Initialization:**
 * - Called automatically by GoogleAnalyticsLauncher component when googleAnalyticsId is available
 * - Can also be called directly from DynamicSatelliteService after fetching satellite data
 * - Safe to call multiple times with same ID
 * - Handles ID changes by reinitializing with new measurement ID
 *
 * **Configuration:**
 * - Dynamic satellites: googleAnalyticsId comes from API response (can be null)
 * - Non-dynamic satellites: googleAnalyticsId set in satellite index.js
 *
 * NOTE: Uses browser storage for user tracking.
 * TODO: Discuss privacy policy, consent requirements, and student tracking with product team.
 *
 * @example
 * // Initialize after fetching dynamic satellite data
 * GoogleAnalyticsService.initGoogleAnalyticsIfApplicable({ googleAnalyticsId: 'G-XXXXXXXXXX' });
 *
 * @see https://developers.google.com/gtagjs/reference/api - gtag.js API Reference
 * @see https://developers.google.com/analytics/devguides/collection/ga4/events - GA4 Events Documentation
 * @see https://developers.google.com/analytics/devguides/collection/ga4 - GA4 Developer Guide
 */
export default class GoogleAnalyticsService {
  static currentGoogleAnalyticsId = null;

  /**
   * Initialize Google Analytics if googleAnalyticsId is provided.
   *
   * Safe to call multiple times - will only reinitialize if the ID changes.
   *
   * @param {Object} options - Configuration options
   * @param {string} options.googleAnalyticsId - Google Analytics measurement ID (e.g. 'G-XXXXXXXXXX')
   */
  static initGoogleAnalyticsIfApplicable = ({ googleAnalyticsId } = {}) => {
    if (!googleAnalyticsId) {
      return;
    }

    // If already initialized with the same ID, skip
    if (this.currentGoogleAnalyticsId === googleAnalyticsId) {
      return;
    }

    // If ID changed, cleanup previous instance
    if (this.currentGoogleAnalyticsId && this.currentGoogleAnalyticsId !== googleAnalyticsId) {
      this.removeGoogleAnalytics();
    }

    this.initGoogleAnalytics({ googleAnalyticsId });
  }

  /**
   * Initialize Google Analytics gtag.js script.
   *
   * @param {Object} options - Configuration options
   * @param {string} options.googleAnalyticsId - Google Analytics measurement ID
   */
  static initGoogleAnalytics = ({ googleAnalyticsId } = {}) => {
    console.info('Initializing Google Analytics with ID:', googleAnalyticsId);
    if (!googleAnalyticsId) {
      console.warn('Google Analytics ID not provided');
      return;
    }

    // Check if script already exists
    const scriptId = 'gtag-script';
    const existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      // Create and load gtag.js script
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`;
      script.async = true;
      document.head.appendChild(script);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
    }

    // Configure with measurement ID
    window.gtag('config', googleAnalyticsId);

    // Update manager and current tracking ID
    googleAnalyticsManager.setGoogleAnalyticsId(googleAnalyticsId);
    this.currentGoogleAnalyticsId = googleAnalyticsId;
  }

  /**
   * Remove Google Analytics scripts and cleanup.
   *
   * Called when googleAnalyticsId changes to cleanup the previous instance.
   */
  static removeGoogleAnalytics = () => {
    // Remove script element
    const scriptId = 'gtag-script';
    const script = document.getElementById(scriptId);
    if (script) {
      script.remove();
    }

    // Cleanup global objects
    delete window.gtag;
    delete window.dataLayer;

    // Clear manager state
    googleAnalyticsManager.clearAll();
    this.currentGoogleAnalyticsId = null;
  }

  // ============================================================================
  // EVENT TRACKING METHODS
  // ============================================================================
  //
  // As product requirements evolve, there will likely be future requirements to
  // track specific user events (e.g. login, assignment submission, resource views,
  // course enrollment, etc.).
  //
  // When those requirements are identified, we can add tracking methods here
  // following the pattern shown in the example below.
  //
  // Note: Google Analytics automatically tracks page views and sessions by
  // default. Custom event tracking methods would be for tracking specific user
  // actions and behaviors.
  //
  // See Google Analytics documentation for event tracking:
  // https://developers.google.com/analytics/devguides/collection/ga4/events
  // ============================================================================

  // /**
  //  * Track custom event.
  //  *
  //  * Generic method for tracking custom events in Google Analytics.
  //  *
  //  * @param {string} eventName - Name of the event
  //  * @param {Object} eventParams - Event parameters
  //  *
  //  * @example
  //  * GoogleAnalyticsService.trackCustomEvent('login', { method: 'google' });
  //  * GoogleAnalyticsService.trackCustomEvent('assignment_submitted', {
  //  *   assignment_id: '123',
  //  *   course_id: '456',
  //  *   score: 95
  //  * });
  //  */
  // static trackCustomEvent = (eventName, eventParams = {}) => {
  //   if (!window.gtag) {
  //     return;
  //   }
  //
  //   window.gtag('event', eventName, eventParams);
  // }
}

registerClass('GoogleAnalyticsService', GoogleAnalyticsService);
